import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import { employeeService, handoverService } from "../services/api";
import moment from "moment";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUser,
  FaCar,
  FaClipboardList,
  FaCheckCircle,
  FaSpinner,
  FaPlus,
  FaEye,
  FaCalendarAlt,
  FaGasPump,
  FaTachometerAlt,
  FaClipboard,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaTimes,
} from "react-icons/fa";
import HandoverForm from "../pages/HandoverForm";
import HandoverDetailsModal from "../components/modals/HandoverDetailsModal";
import AllHandoversModal from "../components/modals/AllHandoversModal";
import EditProfileModal from "../components/modals/EditProfileModal";
import CombinedRentalHandoverModal from "../components/RentalModal/CombinedRentalHandoverModal";

const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [pendingHandovers, setPendingHandovers] = useState([]);
  const [completedHandovers, setCompletedHandovers] = useState([]);
  const [isCombinedModalOpen, setIsCombinedModalOpen] = useState(false); // States for assign partner functionality

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("completed");
  const [animateStats, setAnimateStats] = useState(false);
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showAllHandoversModal, setShowAllHandoversModal] = useState(false);
  const [selectedHandoverId, setSelectedHandoverId] = useState(null);
  const [showHandoverDetailsModal, setShowHandoverDetailsModal] =
    useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Add a state to trigger refreshes
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    avatarUrl: "",
  });
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);

        // Get employee profile
        const profileResponse = await employeeService.getProfile();
        setEmployee(profileResponse.data);
        console.log("Employee Profile:", profileResponse.data);

        // Populate profile data for the edit modal
        setProfileData({
          firstName: profileResponse.data.firstName || "",
          lastName: profileResponse.data.lastName || "",
          email: profileResponse.data.email || "",
          phone: profileResponse.data.phone || "",
          avatarUrl: profileResponse.data.avatarUrl || "",
        });

        // Get pending handovers
        const pendingResponse = await handoverService.getHandovers({
          employee: profileResponse.data._id,
          status: "pending",
          limit: 10,
        });
        setPendingHandovers(pendingResponse.data.handovers);

        // Get completed handovers
        const completedResponse = await handoverService.getHandovers({
          employee: profileResponse.data._id,
          status: "completed",
          limit: 10,
        });
        setCompletedHandovers(completedResponse.data.handovers);

        setLoading(false);
        // Trigger animation after data is loaded
        setTimeout(() => setAnimateStats(true), 300);

        // Check for handoverId in sessionStorage (for redirect from direct URL)
        const storedHandoverId = sessionStorage.getItem("viewHandoverId");
        if (storedHandoverId) {
          setSelectedHandoverId(storedHandoverId);
          setShowHandoverDetailsModal(true);
          // Clear from sessionStorage after using it
          sessionStorage.removeItem("viewHandoverId");
        }
      } catch (err) {
        console.error("Error fetching employee dashboard data:", err);
        setError("Failed to load dashboard data. Please try again.");
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [refreshTrigger]); // Add refreshTrigger as a dependency

  const handleRefreshData = () => {
    // Increment the refresh trigger to cause a re-fetch of data
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleViewHandover = (handoverId) => {
    setSelectedHandoverId(handoverId);
    setShowHandoverDetailsModal(true);
  };

  const handleNewHandover = () => {
    setShowHandoverModal(true);
    window.scrollTo(0, 0);
  };

  const handleViewAllHandovers = () => {
    setShowAllHandoversModal(true);
  };

  const handleEditProfile = () => {
    setShowEditProfileModal(true);
  };

  const handleProfileUpdate = (updatedData) => {
    setEmployee((prevEmployee) => ({
      ...prevEmployee,
      ...updatedData,
    }));

    // Update profile data state as well
    setProfileData({
      firstName: updatedData.firstName || "",
      lastName: updatedData.lastName || "",
      email: updatedData.email || "",
      phone: updatedData.phone || "",
      avatarUrl: updatedData.avatarUrl || "",
    });
  };
  const handleDeleteHandover = (handoverId, deleteInfo) => {
    console.log("Handover deleted:", handoverId, "Delete info:", deleteInfo);

    // If the deleted handover was in the pending list, remove it
    setPendingHandovers((prevHandovers) =>
      prevHandovers.filter((handover) => handover._id !== handoverId)
    );

    // If the deleted handover was in the completed list, remove it
    setCompletedHandovers((prevHandovers) =>
      prevHandovers.filter((handover) => handover._id !== handoverId)
    );

    // Refresh the data to ensure the dashboard displays the latest information
    handleRefreshData();
  };

  const handleMoneyStatusToggle = async (handoverId, currentStatus) => {
    try {
      const newStatus = !currentStatus;

      // Update the backend
      await handoverService.updateMoneyStatus(handoverId, newStatus);

      // Update local state
      setCompletedHandovers((prevHandovers) =>
        prevHandovers.map((handover) =>
          handover._id === handoverId
            ? { ...handover, moneyHandedOver: newStatus }
            : handover
        )
      );

      console.log(
        `Money status updated for handover ${handoverId}: ${newStatus}`
      );
    } catch (error) {
      console.error("Error updating money status:", error);
      // You could add a toast notification here for better UX
    }
  };

  // Card variants for animations
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut",
      },
    }),
  };

  // Stats counter animation for numbers
  const countAnimation = (value) => {
    return animateStats ? value : 0;
  };
  if (loading) {
    return (
      <>
        <Header />
        <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50">
          <motion.div
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="flex flex-col items-center"
          >
            <div className="h-16 w-16 border-t-4 border-b-4 border-blue-600 rounded-full"></div>
          </motion.div>
          <p className="mt-4 text-blue-600 font-medium">Loading dashboard...</p>
        </div>
        <Footer />
      </>
    );
  }
  if (error) {
    return (
      <>
        <Header />
        <div className="container m-auto items-center min-h-screen px-4 py-16">
          <div className="bg-red-100 text-red-800 p-6 rounded-lg shadow-md my-4 border-l-4 border-red-500 flex items-center">
            <div className="mr-4 text-red-500">
              <svg
                className="w-10 h-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-1">
                Error Loading Dashboard
              </h3>
              <p>{error}</p>
              <div className="flex flex-row gap-4 items-center mt-4">
                <button
                  onClick={() => window.location.reload()}
                  className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="mt-2 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
                >
                  Go to Home
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10">
          {/* Welcome Banner */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="bg-gradient-to-r from-blue-600 to-blue-800 shadow-lg rounded-xl p-8 mb-8 text-white relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mt-20 -mr-20"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-10 rounded-full -mb-10 -ml-10"></div>

            <div className="relative z-10">
              <h1 className="text-3xl font-bold mb-2 flex items-center">
                <span className="mr-2">👋</span>Welcome, {employee?.firstName}!
              </h1>
              <p className="text-blue-100 mb-6">
                Here's your activity summary.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="bg-white bg-opacity-20 backdrop-filter backdrop-blur-lg rounded-lg p-4 flex items-center"
                >
                  <div className="w-12 h-12 bg-blue-500 bg-opacity-30 rounded-full flex items-center justify-center mr-4">
                    <FaClipboardList className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-100">Pending Handovers</p>
                    <motion.h3
                      className="text-2xl font-bold"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                    >
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.6 }}
                      >
                        {pendingHandovers.length}
                      </motion.span>
                    </motion.h3>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="bg-white bg-opacity-20 backdrop-filter backdrop-blur-lg rounded-lg p-4 flex items-center"
                >
                  <div className="w-12 h-12 bg-green-500 bg-opacity-30 rounded-full flex items-center justify-center mr-4">
                    <FaCheckCircle className="text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-100">Completed Handovers</p>
                    <motion.h3
                      className="text-2xl font-bold"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5, duration: 0.5 }}
                    >
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.7 }}
                      >
                        {completedHandovers.length}
                      </motion.span>
                    </motion.h3>
                  </div>
                </motion.div>

                <motion.button
                  onClick={handleNewHandover}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className="bg-white text-blue-600 font-semibold rounded-lg p-4 flex items-center justify-center shadow-md"
                >
                  <FaPlus className="mr-2" />
                  Create New Handover
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* Info Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Employee Information Card */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-xl shadow-md overflow-hidden"
            >
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
                <h3 className="font-semibold text-white text-lg flex items-center">
                  <FaUser className="mr-2" />
                  Employee Information
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <FaUser className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Full Name</p>
                    <p className="font-medium text-gray-800">
                      {employee?.firstName} {employee?.lastName}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <FaEnvelope className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email Address</p>
                    <p className="font-medium text-gray-800">
                      {employee?.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3 flex-shrink-0">
                    <FaIdCard className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">ID Number</p>
                    <p className="font-medium text-gray-800">
                      {employee?.idNumber || "Not provided"}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions Card */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-xl shadow-md overflow-hidden"
            >
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4">
                <h3 className="font-semibold text-white text-lg flex items-center">
                  <FaClipboard className="mr-2" />
                  Quick Actions
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {" "}
                  <motion.button
                    onClick={handleNewHandover}
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg flex items-center justify-center font-medium shadow-md"
                  >
                    <FaPlus className="mr-2" />
                    Create New Handover
                  </motion.button>
                  {/* create combined */}
                  <motion.button
                    onClick={() => setIsCombinedModalOpen(true)}
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg flex items-center justify-center font-medium shadow-md"
                  >
                    <FaCar className="mr-2" />
                    Create Combined Handover
                  </motion.button>
                  <motion.button
                    onClick={handleViewAllHandovers}
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg flex items-center justify-center font-medium transition-colors"
                  >
                    <FaClipboardList className="mr-2" />
                    View All Handovers
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {/* Stats Summary Card */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-xl shadow-md overflow-hidden"
            >
              <div className="bg-gradient-to-r from-green-500 to-teal-600 px-6 py-4">
                <h3 className="font-semibold text-white text-lg flex items-center">
                  <FaCheckCircle className="mr-2" />
                  Handover Statistics
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="w-12 h-12 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-2">
                      <FaClipboardList className="text-green-600 text-xl" />
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Pending</p>
                    <motion.p
                      className="text-2xl font-bold text-green-700"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6 }}
                    >
                      {pendingHandovers.length}
                    </motion.p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-100 mx-auto flex items-center justify-center mb-2">
                      <FaCheckCircle className="text-blue-600 text-xl" />
                    </div>
                    <p className="text-sm text-gray-600 mb-1">Completed</p>
                    <motion.p
                      className="text-2xl font-bold text-blue-700"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      {completedHandovers.length}
                    </motion.p>
                  </div>
                </div>

                <div className="mt-6">
                  <p className="text-sm text-gray-500 mb-2">Recent Activity</p>
                  <div className="h-2 bg-gray-200 rounded-full">
                    <motion.div
                      className="h-full bg-gradient-to-r from-green-500 to-teal-600 rounded-full"
                      initial={{ width: "0%" }}
                      animate={{
                        width:
                          pendingHandovers.length > 0
                            ? `${Math.min(100, pendingHandovers.length * 10)}%`
                            : "5%",
                      }}
                      transition={{ delay: 0.5, duration: 1 }}
                    ></motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Handovers Tabs and Tables */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-white shadow-md rounded-xl overflow-hidden"
          >
            <div className="flex border-b">
              <button
                onClick={() => setActiveTab("pending")}
                className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                  activeTab === "pending"
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className="flex items-center justify-center">
                  <FaClipboardList
                    className={`mr-2 ${
                      activeTab === "pending"
                        ? "text-blue-600"
                        : "text-gray-400"
                    }`}
                  />
                  Pending Handovers
                </span>
              </button>

              <button
                onClick={() => setActiveTab("completed")}
                className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${
                  activeTab === "completed"
                    ? "text-green-600 border-b-2 border-green-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <span className="flex items-center justify-center">
                  <FaCheckCircle
                    className={`mr-2 ${
                      activeTab === "completed"
                        ? "text-green-600"
                        : "text-gray-400"
                    }`}
                  />
                  Completed Handovers
                </span>
              </button>
            </div>
            <div className="p-6">
              <AnimatePresence mode="wait">
                {activeTab === "pending" ? (
                  <motion.div
                    key="pending"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {pendingHandovers.length === 0 ? (
                      <motion.div
                        className="text-center py-10"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <div className="mx-auto w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                          <FaClipboardList className="text-gray-400 text-2xl" />
                        </div>
                        <p className="text-gray-500 text-lg">
                          No pending handovers at this moment.
                        </p>
                        <button
                          onClick={handleNewHandover}
                          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto"
                        >
                          <FaPlus className="mr-2" />
                          Create New Handover
                        </button>
                      </motion.div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date & Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Vehicle
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Customer
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {pendingHandovers.map((handover, index) => (
                              <motion.tr
                                key={handover._id}
                                custom={index}
                                initial="hidden"
                                animate="visible"
                                variants={cardVariants}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <FaCalendarAlt className="text-blue-500 mr-2" />
                                    <span className="text-sm text-gray-900">
                                      {moment(handover.handoverDate).format(
                                        "DD/MM/YYYY HH:mm"
                                      )}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <FaCar className="text-blue-500 mr-2" />
                                    <span className="text-sm text-gray-900">
                                      {handover.vehicle?.make}{" "}
                                      {handover.vehicle?.model}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <FaUser className="text-blue-500 mr-2" />
                                    <span className="text-sm text-gray-900">
                                      {handover.customer.name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      handover.handoverType === "pickup"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-blue-100 text-blue-800"
                                    }`}
                                  >
                                    {handover.handoverType === "pickup"
                                      ? "Pickup"
                                      : "Return"}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <motion.button
                                    onClick={() =>
                                      handleViewHandover(handover._id)
                                    }
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg text-sm flex items-center"
                                  >
                                    <FaEye className="mr-1" />
                                    View
                                  </motion.button>
                                </td>
                              </motion.tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    key="completed"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {completedHandovers.length === 0 ? (
                      <motion.div
                        className="text-center py-10"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                      >
                        <div className="mx-auto w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                          <FaCheckCircle className="text-gray-400 text-2xl" />
                        </div>{" "}
                        <p className="text-gray-500 text-lg">
                          No completed handovers yet.
                        </p>
                      </motion.div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date & Time
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Vehicle
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Customer
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Money Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {completedHandovers.map((handover, index) => (
                              <motion.tr
                                key={handover._id}
                                custom={index}
                                initial="hidden"
                                animate="visible"
                                variants={cardVariants}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <FaCalendarAlt className="text-green-500 mr-2" />
                                    <span className="text-sm text-gray-900">
                                      {moment(handover.handoverDate).format(
                                        "DD/MM/YYYY HH:mm"
                                      )}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <FaCar className="text-green-500 mr-2" />
                                    <span className="text-sm text-gray-900">
                                      {handover.vehicle?.make}{" "}
                                      {handover.vehicle?.model}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <FaUser className="text-green-500 mr-2" />
                                    <span className="text-sm text-gray-900">
                                      {handover.customer.name}
                                    </span>
                                  </div>
                                </td>{" "}
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      handover.handoverType === "pickup"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-blue-100 text-blue-800"
                                    }`}
                                  >
                                    {handover.handoverType === "pickup"
                                      ? "Pickup"
                                      : "Return"}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <button
                                      onClick={() =>
                                        handleMoneyStatusToggle(
                                          handover._id,
                                          handover.moneyHandedOver
                                        )
                                      }
                                      className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-200 ease-in-out ${
                                        handover.moneyHandedOver
                                          ? "bg-green-600"
                                          : "bg-gray-300"
                                      }`}
                                    >
                                      <span
                                        className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ease-in-out ${
                                          handover.moneyHandedOver
                                            ? "translate-x-6"
                                            : "translate-x-1"
                                        }`}
                                      />
                                    </button>
                                    <span
                                      className={`ml-3 text-sm font-medium ${
                                        handover.moneyHandedOver
                                          ? "text-green-600"
                                          : "text-gray-500"
                                      }`}
                                    >
                                      {handover.moneyHandedOver
                                        ? "Handed Over"
                                        : "Pending"}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <motion.button
                                    onClick={() =>
                                      handleViewHandover(handover._id)
                                    }
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg text-sm flex items-center"
                                  >
                                    <FaEye className="mr-1" />
                                    View
                                  </motion.button>
                                </td>
                              </motion.tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>{" "}
          </motion.div>
        </div>
      </div>
      <Footer />
      {/* Handover Form Modal */}
      <AnimatePresence>
        {showHandoverModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 flex justify-center items-start pt-4 px-4"
            style={{ backdropFilter: "blur(4px)" }}
            onClick={() => setShowHandoverModal(false)}
          >
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              className="bg-white w-full max-w-6xl rounded-lg shadow-2xl max-h-[90vh] overflow-y-auto relative"
              onClick={(e) => e.stopPropagation()} // Prevent clicks inside the modal from closing it
            >
              <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center z-10">
                <h2 className="text-xl font-bold text-gray-800">
                  Create New Handover
                </h2>
                <button
                  onClick={() => setShowHandoverModal(false)}
                  className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full transition-colors focus:outline-none"
                  aria-label="Close"
                >
                  <FaTimes className="text-gray-700" />
                </button>
              </div>{" "}
              <div className="px-4 py-6 text-black">
                <HandoverForm
                  isModal={true}
                  onClose={() => setShowHandoverModal(false)}
                  onSubmitSuccess={() => {
                    console.log(
                      "Handover submitted successfully, refreshing dashboard data"
                    );
                    handleRefreshData();
                  }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Handover Details Modal */}
      <AnimatePresence>
        {showHandoverDetailsModal && (
          <HandoverDetailsModal
            handoverId={selectedHandoverId}
            onClose={() => setShowHandoverDetailsModal(false)}
            onDelete={handleDeleteHandover}
          />
        )}
      </AnimatePresence>
      {console.log("employee", employee)} {/* All Handovers Modal */}
      <AnimatePresence>
        {showAllHandoversModal && (
          <AllHandoversModal
            employeeId={employee?._id}
            onClose={() => setShowAllHandoversModal(false)}
            onViewHandover={handleViewHandover}
            refreshCount={refreshTrigger}
          />
        )}
      </AnimatePresence>
      {/* Edit Profile Modal */}
      <AnimatePresence>
        {showEditProfileModal && (
          <EditProfileModal
            employee={employee}
            onClose={() => setShowEditProfileModal(false)}
            onProfileUpdate={handleProfileUpdate}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isCombinedModalOpen && (
          <CombinedRentalHandoverModal
            isOpen={isCombinedModalOpen}
            onClose={() => setIsCombinedModalOpen(false)}
            onSuccess={() => {
              setIsCombinedModalOpen(false);
              handleRefreshData();}}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default EmployeeDashboard;
